import { createSlice } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'

import {
    login,
    logout as logoutFromRepository,
    timeToRefresh,
    sendPassowrdReset,
} from '../../repositories/UsersRepository'
import { tagHotjarRecording } from '../../helpers/util'
import { updateGAUserConfig } from '../../utils/sendGAAnalyticsEvent'
import { useUserActions } from './UserSlice'
import { useRequestActions } from './RequestSlice'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import { removeStepsAndRequests } from '../../utils/handleOnDone'
import { deleteAllDashboardData } from '../../utils/deleteIndexDbData'

const session = JSON.parse(localStorage.getItem('session') || '{}')

const initialState = {
    authenticated: session?.authenticated || false,
    role: session?.role || { firstName: 'Guest', lastName: 'User' },
    user: session?.user || 'guest',
    impersonated: session?.impersonated || false,
    impersonatedBy: session?.impersonatedBy || '',
    impersonatedId: session?.impersonatedId ?? '',
    errors: [],
    // The interval ID for the session expiration check.
    expirationCheckTime: session?.expirationCheckTime || '',
    expirationCheckId: session?.expirationCheckId || 0,
    hasExpired: false,
    needReloadProfile: true,
}

const authSlice = createSlice({
    name: 'Auth',
    initialState,
    reducers: {
        loginSuccess(state, { payload }) {
            state.authenticated = payload?.authenticated
            state.role = payload?.role
            state.user = payload?.user
            state.impersonated = false
            state.impersonatedBy = ''
            state.impersonatedId = ''
            state.errors = []
        },
        loginFailure(state, { payload }) {
            state.errors = [payload?.message]
        },
        showTour(state, { payload }) {
            state.user = payload
        },
        expireSession(state) {
            state.authenticated = false
            state.role = 'guest'
            state.user = { firstName: 'Guest', lastName: 'User' }
            state.impersonated = false
            state.impersonatedBy = ''
            state.impersonatedId = ''
            state.hasExpired = false
            state.expirationCheckTime = ''
            state.expirationCheckId = 0
        },
        hasExpired(state) {
            state.hasExpired = true
        },
        impersonateSuccess(state, { payload }) {
            state.authenticated = payload?.authenticated
            state.role = payload?.role
            state.user = payload?.user
            state.impersonated = true
            state.impersonatedBy = payload?.impersonatedBy
            state.impersonatedId = payload?.impersonatedId ?? ''
        },
        stopImpersonationSuccess(state, { payload }) {
            state.authenticated = payload?.authenticated
            state.role = payload?.role
            state.user = payload?.user
            state.impersonated = payload?.impersonated ?? false
            state.impersonatedBy = payload?.impersonatedBy ?? ''
            state.impersonatedId = payload?.impersonatedId ?? ''
        },
        profileSaveSuccess(state, { payload }) {
            state.role = payload?.role
            state.user = payload?.user
        },
        setExpirationCheckTime(state, { payload }) {
            state.expirationCheckTime = payload
        },
        setExpirationCheckID(state, { payload }) {
            state.expirationCheckId = payload
        },
        storeDashboardInfo(state, { payload }) {
            state.user.dashboard = payload
        },
        storeUserCompanyInfo(state, { payload }) {
            state.user.company = payload
        },
        toggleNeedReloadProfile(state, { payload }) {
            state.needReloadProfile = payload
        },
        storeUserLocationPreferences(state, { payload }) {
            state.user.locationPreferences = payload
        },
        storeUserFeatureFlags(state, { payload }) {
            state.user.feature_flags = payload
        },
    },
})

export const { actions } = authSlice
export default authSlice.reducer

export const useAuthActions = () => {
    const dispatch = useDispatch()
    const { formError, formLoading, formSuccess, resetState } = useUserActions()
    const { resetNeedsUpdate } = useRequestActions()
    const { changeLoading } = useGlobalLoading(false)

    const handleLoginSuccess = request => {
        dispatch(authSlice.actions.loginSuccess(request))
    }

    const handleLoginFailure = request => {
        return dispatch(authSlice.actions.loginFailure(request))
    }

    const handleShowTour = request => {
        return dispatch(authSlice.actions.showTour(request))
    }

    const handleExpireSession = () => {
        return dispatch(authSlice.actions.expireSession())
    }

    const handleHasExpired = () => {
        return dispatch(authSlice.actions.hasExpired())
    }

    const handleImpersonateSuccess = request => {
        resetNeedsUpdate()
        dispatch(authSlice.actions.impersonateSuccess(request))
    }

    const handleStopImpersonationSuccess = sessionData => {
        resetNeedsUpdate()
        const session = JSON.stringify(sessionData)
        localStorage.setItem('session', session)
        dispatch(authSlice.actions.stopImpersonationSuccess(sessionData))
        updateGAUserConfig()
    }

    const handleSetExpirationCheckTime = request => {
        return dispatch(authSlice.actions.setExpirationCheckTime(request))
    }

    const handleSetExpirationCheckID = request => {
        return dispatch(authSlice.actions.setExpirationCheckID(request))
    }

    const handleUpdateShowTour = bool => {
        var session = JSON.parse(localStorage.getItem('session'))
        session.user.show_tour = bool

        var sessionObj = JSON.stringify(session)
        localStorage.setItem('session', sessionObj)
        dispatch(authSlice.actions.showTour(session.user))
    }

    const handleProfileSuccess = sessionData => {
        var session = JSON.stringify(sessionData)
        localStorage.setItem('session', session)
        dispatch(authSlice.actions.profileSaveSuccess(sessionData))
    }

    const handleAuthorize = request => {
        changeLoading(true)
        return login(request)
            .then(response => {
                const { session } = response.data

                if (!session) {
                    throw new Error('No session data received')
                }

                localStorage.setItem('session', JSON.stringify(session))
                localStorage.setItem('lastLogin', Date.now().toString())

                timeToRefresh().then(timeToRefresh => {
                    const refreshTime = timeToRefresh?.data?.time_to_refresh
                    if (refreshTime) {
                        localStorage.setItem(
                            'timeToRefresh',
                            refreshTime.toString(),
                        )
                    }

                    if (window.Intercom != null) {
                        window.Intercom('shutdown')
                    }

                    resetNeedsUpdate()
                    dispatch(authSlice.actions.loginSuccess(session))
                    updateGAUserConfig()
                    changeLoading(false)
                    resetState()
                })
            })
            .catch(err => {
                tagHotjarRecording('API Error')
                dispatch(authSlice.actions.loginFailure(err?.response?.data))
                changeLoading(false)
            })
    }

    const isSessionExpiredOffline = () => {
        const lastLogin = localStorage.getItem('lastLogin')
        const timeToRefresh = localStorage.getItem('timeToRefresh')
        if (!lastLogin || !timeToRefresh) {
            return true
        }
        const lastLoginTime = Number(lastLogin)
        if (isNaN(lastLoginTime)) {
            return true
        }
        const sessionTimeout = Number(timeToRefresh) * 60 * 1000
        const elapsedTime = Date.now() - lastLoginTime
        return elapsedTime >= sessionTimeout
    }

    const clearSessionStorage = () => {
        removeStepsAndRequests()
        localStorage.removeItem('authToken')
        localStorage.removeItem('session')
        localStorage.removeItem('expirationTime')
        localStorage.removeItem('reviewRequests')
        localStorage.removeItem('lastLogin')
        localStorage.removeItem('timeToRefresh')
        deleteAllDashboardData()
    }

    const handleOfflineSessionCheck = () => {
        if (isSessionExpiredOffline()) {
            clearSessionStorage()
            window.location.reload()
        }
    }

    const handleLogout = currentCheckId => {
        logoutFromRepository()
            .then(() => {
                clearSessionStorage()

                if (currentCheckId !== 0) {
                    clearInterval(currentCheckId)
                }
                dispatch(authSlice.actions.expireSession())
                updateGAUserConfig()
            })
            .catch(error => {
                console.error('Logout error:', error)
            })
    }

    const handleForgotPassword = email => {
        dispatch(formLoading(true))
        changeLoading(true)

        sendPassowrdReset(email)
            .then(response => {
                if (response.status == 200) {
                    formSuccess()
                    changeLoading(false)
                }
            })
            .catch(error => {
                tagHotjarRecording('API Error')
                var errMsg =
                    error.response.data.errors &&
                    error.response.data.errors.email[0]
                        ? error.response.data.errors.email[0]
                        : error.response.data.message
                formLoading(false)
                formError(errMsg)
                changeLoading(false)
            })
    }

    const storeDashboardInfo = data => {
        return dispatch(actions.storeDashboardInfo(data))
    }

    const storeUserCompanyInfo = data => {
        return dispatch(actions.storeUserCompanyInfo(data))
    }

    const toggleNeedReloadProfile = data => {
        return dispatch(actions.toggleNeedReloadProfile(data))
    }

    const storeUserLocationPreferences = data => {
        return dispatch(actions.storeUserLocationPreferences(data))
    }

    const storeUserFeatureFlags = data => {
        return dispatch(actions.storeUserFeatureFlags(data))
    }

    return {
        handleLoginSuccess,
        handleLoginFailure,
        handleShowTour,
        handleExpireSession,
        handleHasExpired,
        handleImpersonateSuccess,
        handleStopImpersonationSuccess,
        handleSetExpirationCheckTime,
        handleSetExpirationCheckID,
        handleUpdateShowTour,
        handleProfileSuccess,
        handleAuthorize,
        handleOfflineSessionCheck,
        handleLogout,
        handleForgotPassword,
        storeDashboardInfo,
        storeUserCompanyInfo,
        storeUserLocationPreferences,
        toggleNeedReloadProfile,
        storeUserFeatureFlags,
    }
}
